import React, { useState } from 'react';
import { Btn } from './ui';
import { C } from '../data/constants';
import { signIn, signUp } from '../lib/db';
import { isConfigured } from '../lib/supabase';

const INPUT = {
  width: '100%', padding: '14px', marginBottom: 12,
  background: '#fff', border: `2px solid ${C.border}`,
  borderRadius: 13, color: C.text, fontSize: 14,
  fontFamily: 'Roboto, sans-serif', outline: 'none',
  boxSizing: 'border-box',
};

export default function Auth({ initialMode = 'login', onSuccess, onBack }) {
  const [mode, setMode]               = useState(initialMode);
  const [email, setEmail]             = useState('');
  const [pw, setPw]                   = useState('');
  const [name, setName]               = useState('');
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [confirmSent, setConfirmSent] = useState(false);

  async function handleSubmit() {
    setError('');
    if (!email.trim() || !pw) { setError('Email and password are required.'); return; }
    if (mode === 'signup' && !name.trim()) { setError('Please choose a display name.'); return; }
    setLoading(true);
    try {
      if (mode === 'signup') {
        const data = await signUp(email.trim(), pw, name.trim());
        if (!data.session) { setConfirmSent(true); return; }
      } else {
        await signIn(email.trim(), pw);
      }
      onSuccess();
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isConfigured) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚙️</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: C.text }}>Backend not configured</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 8 }}>
          Add your Supabase credentials to <strong>.env</strong>:
        </div>
        <div style={{ background: '#F0F0F0', borderRadius: 10, padding: '10px 16px', fontSize: 12, fontFamily: 'monospace', color: C.text, marginBottom: 20, textAlign: 'left', lineHeight: 1.9 }}>
          REACT_APP_SUPABASE_URL=...<br />
          REACT_APP_SUPABASE_ANON_KEY=...
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.6 }}>
          Then run the SQL in <strong>supabase-schema.sql</strong><br />
          in your Supabase → SQL Editor.
        </div>
        <Btn variant="white" onClick={onBack}>← Back</Btn>
      </div>
    );
  }

  if (confirmSent) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>📨</div>
        <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 8 }}>Check your email!</div>
        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.7, marginBottom: 24 }}>
          We sent a confirmation link to<br /><strong>{email}</strong>.<br />
          Click it to activate your account, then sign in.
        </div>
        <Btn variant="green" onClick={() => { setConfirmSent(false); setMode('login'); }}>🔑 Sign In</Btn>
        <Btn variant="white" onClick={onBack}>← Back to App</Btn>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: '0 1.5rem 2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 0 24px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted, padding: '0 12px 0 0', lineHeight: 1 }}>←</button>
        <div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>KABAAK</div>
          <div style={{ fontSize: 12, color: C.muted }}>Sync progress · Live duels · Global ranks</div>
        </div>
      </div>

      {/* Mode tabs */}
      <div style={{ display: 'flex', background: '#F5F0EE', borderRadius: 14, padding: 4, marginBottom: 24 }}>
        {[['login', '🔑 Sign In'], ['signup', '🌍 Create Account']].map(([m, label]) => (
          <div key={m}
            onClick={() => { setMode(m); setError(''); }}
            style={{
              flex: 1, textAlign: 'center', padding: '10px 8px',
              borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              background: mode === m ? '#fff' : 'transparent',
              color: mode === m ? C.amber : C.muted,
              boxShadow: mode === m ? '0 2px 6px rgba(0,0,0,.08)' : 'none',
              transition: 'all .15s',
            }}>
            {label}
          </div>
        ))}
      </div>

      {mode === 'signup' && (
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Display name (shown on leaderboard)"
          style={INPUT}
        />
      )}
      <input
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Email address"
        type="email"
        autoComplete="email"
        style={INPUT}
      />
      <input
        value={pw}
        onChange={e => setPw(e.target.value)}
        placeholder={mode === 'signup' ? 'Create a password' : 'Password'}
        type="password"
        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        style={INPUT}
        onKeyDown={e => e.key === 'Enter' && !loading && handleSubmit()}
      />

      {error && (
        <div style={{ background: '#FFF0EC', border: `2px solid #FFB0A0`, borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.coral, marginBottom: 10, lineHeight: 1.5 }}>
          ⚠️ {error}
        </div>
      )}

      <Btn variant="green" onClick={handleSubmit} style={{ opacity: loading ? 0.7 : 1 }}>
        {loading ? '⏳ Please wait…' : mode === 'login' ? '🔑 Sign In' : '🚀 Create Account'}
      </Btn>

      {mode === 'login' && (
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: C.muted }}>
          No account yet?{' '}
          <span
            onClick={() => { setMode('signup'); setError(''); }}
            style={{ color: C.amber, fontWeight: 700, cursor: 'pointer' }}>
            Create one free →
          </span>
        </div>
      )}

      <div style={{ background: '#F8F4F0', borderRadius: 12, padding: 14, marginTop: 24, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
        🔒 <strong>Your progress is always saved locally — no account required.</strong><br />
        An account syncs your scores, ranks, and titles across all your devices and unlocks live duels against other players.
      </div>
    </div>
  );
}
